import {HttpClient} from '@angular/common/http';
import {DestroyRef, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService, TranslateStore, type TranslationObject} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import type {ApiResponse} from '../../auth/auth.models';
import {buildApiUrl} from '../api/api-url';
import {AppConfigResponseDto} from './app-config-response-dto';

const DEFAULT_APP_DISPLAY_NAME = 'Invemory';
const APP_DISPLAY_NAME_TOKEN = '__APP_DISPLAY_NAME__';
export const DEFAULT_IMAGE_UPLOAD_TARGET_MAX_BYTES = 512000;

@Injectable({
  providedIn: 'root',
})
export class ApplicationIdentityService {
  readonly appDisplayName = signal(DEFAULT_APP_DISPLAY_NAME);
  readonly imageUploadTargetMaxBytes = signal(DEFAULT_IMAGE_UPLOAD_TARGET_MAX_BYTES);

  private initialization?: Promise<void>;
  private readonly translationTemplates = new Map<string, TranslationObject>();

  constructor(
    private readonly httpClient: HttpClient,
    private readonly translateService: TranslateService,
    private readonly translateStore: TranslateStore,
    destroyRef: DestroyRef,
  ) {
    this.applyDocumentTitle(DEFAULT_APP_DISPLAY_NAME);
    this.captureLoadedTranslation(this.translateService.getCurrentLang());
    this.captureLoadedTranslation(this.translateService.getFallbackLang());
    this.applyTranslations(DEFAULT_APP_DISPLAY_NAME);

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(({lang, translations}) => {
        if (!this.translationTemplates.has(lang)) {
          this.translationTemplates.set(lang, this.cloneTranslation(translations));
        }

        this.applyTranslation(lang, this.appDisplayName());
      });
  }

  initialize(): Promise<void> {
    this.initialization ??= this.loadAppDisplayName();
    return this.initialization;
  }

  async refreshImageUploadTarget(): Promise<number> {
    await this.loadAppDisplayName();
    return this.imageUploadTargetMaxBytes();
  }

  private async loadAppDisplayName(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.httpClient.get<ApiResponse<AppConfigResponseDto>>(buildApiUrl('/v1/system/app-config')),
      );
      const target = response.success ? response.data?.imageUploadTargetMaxBytes : undefined;
      if (typeof target === 'number' && Number.isInteger(target) && target >= 50 * 1024 && target <= 20 * 1024 * 1024) {
        this.imageUploadTargetMaxBytes.set(target);
      }
      const appDisplayName = response.success ? response.data?.appDisplayName?.trim() : '';
      if (!appDisplayName) {
        return;
      }

      const previousName = this.appDisplayName();
      this.appDisplayName.set(appDisplayName);
      this.applyDocumentTitle(appDisplayName, previousName);
      this.applyTranslations(appDisplayName);
    } catch {
      // Keep the stable local fallback when runtime configuration is unavailable.
    }
  }

  private applyDocumentTitle(appDisplayName: string, previousName = DEFAULT_APP_DISPLAY_NAME): void {
    if (typeof document === 'undefined') return;
    // A public route owns its descriptive title, including during a late config response.
    if (document.title && document.title !== DEFAULT_APP_DISPLAY_NAME && document.title !== previousName) return;
    document.title = appDisplayName;
  }

  private captureLoadedTranslation(language: string | null): void {
    if (!language || this.translationTemplates.has(language)) {
      return;
    }

    const translations = this.translateStore.getTranslations(language);
    if (!translations || !Object.keys(translations).length) {
      return;
    }

    this.translationTemplates.set(language, this.cloneTranslation(translations));
  }

  private applyTranslations(appDisplayName: string): void {
    for (const language of this.translationTemplates.keys()) {
      this.applyTranslation(language, appDisplayName);
    }
  }

  private applyTranslation(language: string, appDisplayName: string): void {
    const template = this.translationTemplates.get(language);
    if (!template) {
      return;
    }

    this.translateService.setTranslation(
      language,
      this.replaceApplicationNameToken(template, appDisplayName) as TranslationObject,
    );
  }

  private cloneTranslation(value: unknown): TranslationObject {
    return this.replaceApplicationNameToken(value, APP_DISPLAY_NAME_TOKEN) as TranslationObject;
  }

  private replaceApplicationNameToken(value: unknown, appDisplayName: string): unknown {
    if (typeof value === 'string') {
      return value.split(APP_DISPLAY_NAME_TOKEN).join(appDisplayName);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.replaceApplicationNameToken(item, appDisplayName));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce<Record<string, unknown>>((translation, [key, item]) => {
        translation[key] = this.replaceApplicationNameToken(item, appDisplayName);
        return translation;
      }, {});
    }

    return value;
  }
}
